import { useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "critter.transform-display-preferences.v1";
const SYNC_EVENT = "critter:transform-display-preferences";

const DEFAULT_PREFERENCES = {
  positionMode: "local",
  rotationMode: "degrees",
};

const normalizePositionMode = (value) => (value === "global" ? "global" : "local");
const normalizeRotationMode = (value) => (value === "quaternion" ? "quaternion" : "degrees");

const normalizePreferences = (value) => ({
  positionMode: normalizePositionMode(value?.positionMode),
  rotationMode: normalizeRotationMode(value?.rotationMode),
});

const readStoredPreferences = () => {
  if (typeof window === "undefined") return DEFAULT_PREFERENCES;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    return normalizePreferences(JSON.parse(raw));
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

const writeStoredPreferences = (preferences) => {
  if (typeof window === "undefined") return;
  const normalized = normalizePreferences(preferences);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    window.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: normalized }));
  } catch {
    // Ignore write failures (private mode, quota, etc).
  }
};

export const useTransformDisplayPreferences = () => {
  const [preferences, setPreferences] = useState(() => readStoredPreferences());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleStorage = (event) => {
      if (event.key !== STORAGE_KEY) return;
      setPreferences(readStoredPreferences());
    };

    const handleSyncEvent = (event) => {
      const next = normalizePreferences(event?.detail || {});
      setPreferences(next);
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener(SYNC_EVENT, handleSyncEvent);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(SYNC_EVENT, handleSyncEvent);
    };
  }, []);

  const actions = useMemo(
    () => ({
      setPositionMode: (mode) => {
        const normalizedMode = normalizePositionMode(mode);
        setPreferences((prev) => {
          const next = { ...prev, positionMode: normalizedMode };
          writeStoredPreferences(next);
          return next;
        });
      },
      setRotationMode: (mode) => {
        const normalizedMode = normalizeRotationMode(mode);
        setPreferences((prev) => {
          const next = { ...prev, rotationMode: normalizedMode };
          writeStoredPreferences(next);
          return next;
        });
      },
    }),
    []
  );

  return {
    positionMode: preferences.positionMode,
    rotationMode: preferences.rotationMode,
    ...actions,
  };
};
